import { DropdownMenuTrigger } from '@radix-ui/react-dropdown-menu'
import { CheckedAddress } from '@sparkdotfi/common-universal'
import { ExternalLinkIcon, UnplugIcon } from 'lucide-react'
import { useState } from 'react'

import { TopbarWalletAddressDisplay } from './components/TopbarWalletAddressDisplay'
import { TopbarWalletButton } from './TopbarWalletButton'
import { EnsName } from '@/domain/types/EnsName'
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/ui/atoms/dialog/Dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuItemIcon,
  DropdownMenuSeparator,
} from '@/ui/atoms/dropdown/DropdownMenu'
import { ExternalLink } from '@/ui/atoms/link/Link'
import { MenuItem, MenuItemIcon } from '@/ui/atoms/menu-item/MenuItem'
import { testIds } from '@/ui/utils/testIds'

export type WalletMode = 'sandbox' | 'connected' | 'watch-wallet'

export interface TopbarWalletDropdownTriggerInfo {
  mode: WalletMode
  avatar: string
  address: CheckedAddress
  ensName?: EnsName
}

export interface TopbarWalletDropdownContentInfo {
  mode: WalletMode
  walletIcon: string
  address: CheckedAddress
  onDisconnect: () => void
  blockExplorerAddressLink: string | undefined
}

export interface TopbarWalletDropdownProps {
  dropdownTriggerInfo: TopbarWalletDropdownTriggerInfo
  dropdownContentInfo: TopbarWalletDropdownContentInfo
  isMobileDisplay: boolean
}

export function TopbarWalletDropdown({
  dropdownTriggerInfo,
  dropdownContentInfo,
  isMobileDisplay,
}: TopbarWalletDropdownProps) {
  const [open, setOpen] = useState(false)

  const { address, blockExplorerAddressLink, walletIcon, onDisconnect, mode } = dropdownContentInfo

  const triggerButton = <TopbarWalletButton open={open} {...dropdownTriggerInfo} />

  if (dropdownTriggerInfo.mode === 'sandbox') {
    return triggerButton
  }

  function handleDisconnect() {
    setOpen(false)
    onDisconnect()
  }

  const isWatchWalletMode = mode === 'watch-wallet'
  const disconnectLabel = isWatchWalletMode ? 'Exit watch wallet mode' : 'Disconnect'
  const disconnectTestId = isWatchWalletMode
    ? testIds.component.TopbarWallet.exitWatchWalletModeButton
    : testIds.component.TopbarWallet.disconnectButton

  if (isMobileDisplay) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>{triggerButton}</DialogTrigger>
        <DialogContent overlayVariant="default" contentVerticalPosition="bottom" className="gap-1.5">
          <DialogTitle className="pt-2 pb-4">Wallet</DialogTitle>
          <TopbarWalletAddressDisplay walletIcon={walletIcon} address={address} mode={mode} />

          <MenuItem asChild>
            <button className="cursor-pointer" onClick={handleDisconnect} data-testid={disconnectTestId}>
              <MenuItemIcon icon={UnplugIcon} />
              {disconnectLabel}
            </button>
          </MenuItem>

          {blockExplorerAddressLink && (
            <>
              <div className="border-primary border-t" />

              <MenuItem asChild className="cursor-pointer">
                <ExternalLink to={blockExplorerAddressLink} variant="unstyled">
                  <MenuItemIcon icon={ExternalLinkIcon} />
                  View on Explorer
                </ExternalLink>
              </MenuItem>
            </>
          )}
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="flex w-60 flex-col gap-1.5 p-1 text-primary">
        <TopbarWalletAddressDisplay walletIcon={walletIcon} address={address} mode={mode} />

        <DropdownMenuItem asChild>
          <button className="cursor-pointer" onClick={handleDisconnect} data-testid={disconnectTestId}>
            <DropdownMenuItemIcon icon={UnplugIcon} />
            {disconnectLabel}
          </button>
        </DropdownMenuItem>

        {blockExplorerAddressLink && (
          <>
            <DropdownMenuSeparator className="mx-1" />

            <DropdownMenuItem asChild className="cursor-pointer">
              <ExternalLink to={blockExplorerAddressLink} variant="unstyled">
                <DropdownMenuItemIcon icon={ExternalLinkIcon} />
                View on Explorer
              </ExternalLink>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

TopbarWalletDropdown.displayName = 'TopbarWalletDropdown'
